import React, { useState, useMemo } from 'react';
import { builds, keyboards, keycaps, switches, plates } from './dataStore';
import { MultiSelectDropdown, SingleSelectDropdown } from './components/Dropdowns';
import { Button } from './components/Button';

const buildList = Object.values(builds).reverse();
const keyboardList = Array.from(new Set(Object.values(keyboards).map((kbd) => kbd.name))); //to remove duplicates(same name keyboards)
const keycapList = Object.values(keycaps).sort((a, b) => a.name.localeCompare(b.name));
const switchList = Object.values(switches).sort((a, b) => a.name.localeCompare(b.name));


function SoundtestsPage() {
	const [selectedSwitches, setSelectedSwitches] = useState([]);
	const [selectedKeycaps, setSelectedKeycaps] = useState([]);
	const [selectedKeyboards, setSelectedKeyboards] = useState([]);
	const [hasFoam, setHasFoam] = useState('all'); // 'all', 'yes', 'no'
	const [visibleCount, setVisibleCount] = useState(6);
	const filteredBuilds = useMemo(() => buildList.filter((build) => {
		if (!build.hasSoundtest) return false;
		const foamPresent = build.foam && build.foam.length > 0;
		if (hasFoam === 'yes' && !foamPresent) return false;
		if (hasFoam === 'no' && foamPresent) return false;
		const keyboardName = keyboards[build.keyboardId]?.name;
		return (
			(selectedKeyboards.length === 0 || selectedKeyboards.includes(keyboardName)) &&
			(selectedSwitches.length === 0 || selectedSwitches.includes(build.switchId)) &&
			(selectedKeycaps.length === 0 || selectedKeycaps.includes(build.keycapId))
		);
	}), [selectedSwitches, selectedKeycaps, selectedKeyboards, hasFoam]);

	const visibleBuilds = filteredBuilds.slice(0, visibleCount);
	const hasMore = visibleCount < filteredBuilds.length;

	return (
		<div className='min-h-screen bg-[#111112] py-24 px-4 relative overflow-hidden'>
			{/* Ambient blurred backdrop */}
			<div
				aria-hidden='true'
				className='pointer-events-none select-none fixed inset-0 w-full h-full z-0'
				style={{
					backgroundImage: "url('/pictures/soundtest_bg.JPG')",
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					filter: 'blur(12px) brightness(0.5)',
					WebkitFilter: 'blur(12px) brightness(0.5)',
					zIndex: 0,
				}}
			/>
			<div className='relative z-10'>
				<h1 className='text-3xl font-bold text-white mb-6 text-center drop-shadow'>Sound Tests</h1>
				<p className='text-gray-300 mb-12 text-center text-base max-w-2xl mx-auto'>
					Explore sound tests for different keyboard builds, switches, foams, and more. Use the filters to find the exact sound profile you're looking for.
				</p>
				<div className='flex flex-wrap gap-6 justify-center mb-10'>
					<MultiSelectDropdown label='All Keyboards' options={keyboardList} selected={selectedKeyboards} setSelected={setSelectedKeyboards} />
					<MultiSelectDropdown label='All Switches' options={switchList} selected={selectedSwitches} setSelected={setSelectedSwitches} />
					<MultiSelectDropdown label='All Keycaps' options={keycapList} selected={selectedKeycaps} setSelected={setSelectedKeycaps} />
					<SingleSelectDropdown label='Foam' options={[
						{ id: 'all', name: 'Any Foam' },
						{ id: 'yes', name: 'Has Foam' },
						{ id: 'no', name: 'No Foam' }
					]} selected={hasFoam} setSelected={setHasFoam} />
				</div>
				<div className='grid gap-8 md:grid-cols-3 sm:grid-cols-2 grid-cols-1 mt-8'>
					{visibleBuilds.length === 0 && (
						<p className='text-gray-400 col-span-full text-center text-lg animate-pulse'>No builds match the selected filters.</p>
					)}
					{visibleBuilds.map((build) => {
						const kbd = keyboards[build.keyboardId];
						const kc = keycaps[build.keycapId];
						const sw = switches[build.switchId];
						const pl = plates[build.plateId];
						const firstYoutube = build.youtube;
						const cardContent = (
							<div className='bg-black/80 border border-gray-800 shadow-xl rounded-lg p-6 flex flex-col gap-3 hover:scale-105 transition-transform duration-300 cursor-pointer'>
								<img src={build.thumbnail || "/pictures/home_bg.JPG"} alt={build.name} className='w-full h-40 object-cover rounded mb-2'/>
								<h2 className='text-xl font-bold text-white mb-1'>{build.name}</h2>
								<p className='text-gray-300 text-base'>
									<span className='font-semibold'>Keyboard:</span> {kbd?.name || '-'}<br/>
									<span className='font-semibold'>Keycaps:</span> {kc?.name || '-'}<br/>
									<span className='font-semibold'>Switches:</span> {sw?.name || '-'}<br/>
									<span className='font-semibold'>Plate:</span> {pl?.material || '-'}<br/>
									<span className='font-semibold'>Foam:</span> {build.foam && build.foam.length > 0 ? build.foam.join(' + ') : 'No foam'}<br/>
									<span className='font-semibold'>Mount:</span> {build.mount || '-'}
								</p>
								{build.soundDescriptors?.length > 0 && (
									<div className='flex flex-wrap gap-2 mt-2'>
										{build.soundDescriptors.map((d) => (
											<span key={d} className='bg-blue-900/60 text-blue-200 text-xs px-2 py-1 rounded-full'>{d}</span>
										))}
									</div>
								)}
							</div>
						);
						return firstYoutube ? (
							<a
								href={firstYoutube}
								target="_blank"
								rel="noopener noreferrer"
								key={build.id}
								style={{textDecoration: 'none'}}
							>
								{cardContent}
							</a>
						) : null;
					})}
				</div>
				{hasMore && (
					<div className='flex justify-center mt-8'>
						<Button onClick={() => setVisibleCount((c) => c + 6)} children="Load More" className="text-white font-semibold px-6 py-2" />
					</div>
				)}
			</div>
		</div>
	);
}

export default SoundtestsPage;
